import { normalizeRefundTravelerType } from "../../data/flight/order/getRefundAdminGuidance";

export const REFUND_AMOUNT_NAME = "Base Return";
export const REFUND_TAX_NAME = "Tax Return";
export const REFUND_PENALTY_NAME = "Penalty";
export const REFUND_SUPPLIER_FEE_NAME = "Supplier Fee";
export const REFUND_DEDUCTIBLE_NAME = "Deductible";
/** FE estimate line (first quote) — derived from booking appliedAdjustments. */
export const REFUND_BOOKING_MARKUP_NAME = "Booking Markup";
/** Backend line after setRefundPrice — platformMarkup split to this pax type. */
export const REFUND_INTRAVERSE_MARKUP_NAME = "Intraverse Markup";

function roundMoney(n) {
  return Math.round(Math.max(0, Number(n) || 0) * 100) / 100;
}

function roundMoneySigned(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function parseMoneyField(price) {
  if (typeof price !== "string") return Number(price || 0);
  const numeric = parseFloat(
    price.replace(/^[A-Za-z\s]+/, "").replaceAll(",", ""),
  );
  return Number.isFinite(numeric) ? numeric : 0;
}

export function isRefundAmountLine(name) {
  return /base return|refund amount|refundable value/i.test(name || "");
}

export function isRefundTaxLine(name) {
  return (
    /tax return/i.test(name || "") ||
    /^tax$/i.test(name || "") ||
    /refundable tax/i.test(name || "")
  );
}

export function isRefundPenaltyLine(name) {
  return (
    /^penalty$/i.test(name || "") ||
    /refund charge|refund fee/i.test(name || "")
  );
}

export function isRefundSupplierFeeLine(name, row) {
  if (row?.isSupplierFee) return true;
  return /supplier fee/i.test(name || "");
}

export function isRefundDeductibleLine(name) {
  return (
    /^deductible$/i.test(name || "") ||
    /non-?refundable (ticket|value|amount)?$/i.test(name || "")
  );
}

export function isBookingMarkupDisplayLine(rowOrName) {
  const name =
    typeof rowOrName === "string" ? rowOrName : rowOrName?.name || "";
  return (
    /^booking markup$/i.test(name) ||
    /^booking markdown$/i.test(name) ||
    /^original (booking )?markup$/i.test(name)
  );
}

export function isIntraverseMarkupLine(rowOrName) {
  const name =
    typeof rowOrName === "string" ? rowOrName : rowOrName?.name || "";
  return (
    /^intraverse markup$/i.test(name) ||
    /^platform markup$/i.test(name) ||
    /^platform markup returned$/i.test(name)
  );
}

/** Markup add-back lines (FE estimate or backend Intraverse Markup). */
export function isRefundMarkupAddBackLine(rowOrName) {
  return (
    isBookingMarkupDisplayLine(rowOrName) ||
    isIntraverseMarkupLine(rowOrName)
  );
}

/**
 * Prefer backend Intraverse Markup when present on the breakdown.
 * @returns {number|null} signed amount, or null if not on the quote yet
 */
export function getIntraverseMarkupFromBreakdown(breakDown) {
  const row = (breakDown || []).find((r) => isIntraverseMarkupLine(r));
  if (!row) return null;
  const raw = Number(
    row?.amount != null && row?.amount !== ""
      ? row.amount
      : row?.airlineAmount ?? 0,
  );
  if (!Number.isFinite(raw)) return null;
  return roundMoneySigned(raw);
}

/**
 * Markup added back into the estimate.
 * Backend Intraverse Markup wins once returned; otherwise FE booking estimate.
 */
export function resolveRefundMarkupAddBack({
  breakDown,
  unitPrice,
  unitBase = null,
  adjustment = null,
  /** Explicit backend amount when stripped out of breakDown into item field */
  intraverseMarkup = null,
} = {}) {
  const fromBreakdown = getIntraverseMarkupFromBreakdown(breakDown);
  const fromField =
    intraverseMarkup != null && intraverseMarkup !== ""
      ? Number(intraverseMarkup)
      : NaN;
  const backend =
    fromBreakdown != null
      ? fromBreakdown
      : Number.isFinite(fromField)
        ? roundMoneySigned(fromField)
        : null;

  if (backend != null) {
    return {
      heldAdjustment: roundMoney(Math.abs(backend)),
      type: backend < 0 ? "Markdown" : backend > 0 ? "Markup" : null,
      source: "backend",
      label: REFUND_INTRAVERSE_MARKUP_NAME,
    };
  }

  const fe = reverseOriginalAdjustment(unitPrice, adjustment, unitBase);
  if (!fe.type || !(fe.heldAdjustment > 0)) {
    return {
      heldAdjustment: 0,
      type: null,
      source: "none",
      label: REFUND_BOOKING_MARKUP_NAME,
    };
  }
  return {
    heldAdjustment: fe.heldAdjustment,
    type: fe.type,
    source: "estimate",
    label: REFUND_BOOKING_MARKUP_NAME,
  };
}

export function isComputedDeductibleLine(row) {
  return isRefundDeductibleLine(row?.name);
}

/** Auto lines Ops do not edit (Deductible + markup add-back lines). */
export function isAutoGeneratedBreakdownLine(row) {
  return (
    isComputedDeductibleLine(row) || isRefundMarkupAddBackLine(row)
  );
}

/**
 * Booking markup/markdown line (isDeducted: false).
 * Percentage applies to base fare only. Amount is the held slice that was
 * stripped to get original unit price, then added back into the estimate.
 */
export function buildBookingMarkupBreakdownRow(
  unitPrice,
  adjustment,
  unitBase = null,
) {
  const { heldAdjustment, type } = reverseOriginalAdjustment(
    unitPrice,
    adjustment,
    unitBase,
  );
  if (!type || !(heldAdjustment > 0)) return null;
  const signed =
    type === "Markdown"
      ? roundMoneySigned(-heldAdjustment)
      : roundMoneySigned(heldAdjustment);
  return {
    name: REFUND_BOOKING_MARKUP_NAME,
    amount: signed,
    airlineAmount: signed,
    isDeducted: false,
  };
}

export function displayRefundLineName(name) {
  if (isRefundAmountLine(name)) return REFUND_AMOUNT_NAME;
  if (isRefundTaxLine(name)) return REFUND_TAX_NAME;
  if (isRefundPenaltyLine(name)) return REFUND_PENALTY_NAME;
  if (isRefundSupplierFeeLine(name)) return REFUND_SUPPLIER_FEE_NAME;
  if (isRefundDeductibleLine(name)) return REFUND_DEDUCTIBLE_NAME;
  if (isIntraverseMarkupLine(name)) return REFUND_INTRAVERSE_MARKUP_NAME;
  if (isBookingMarkupDisplayLine(name)) return REFUND_BOOKING_MARKUP_NAME;
  return name || "";
}

export function getLineAmount(breakDown, matcher) {
  return (breakDown || []).reduce((sum, row) => {
    if (!matcher(row?.name, row)) return sum;
    return sum + Number(row?.amount || 0);
  }, 0);
}

export function normalizeRefundBreakdown(breakDown) {
  const rows = Array.isArray(breakDown) ? [...breakDown] : [];
  const hasAmount = rows.some((row) => isRefundAmountLine(row?.name));
  const hasTax = rows.some((row) => isRefundTaxLine(row?.name));
  if (!hasAmount) {
    rows.unshift({
      name: REFUND_AMOUNT_NAME,
      amount: 0,
      isDeducted: false,
    });
  }
  if (!hasTax) {
    rows.push({ name: REFUND_TAX_NAME, amount: 0, isDeducted: false });
  }
  return rows.map((row) => ({
    name: displayRefundLineName(row?.name) || row?.name,
    amount: Number(row?.amount || 0),
    isDeducted: row?.isDeducted,
    airlineAmount: row?.airlineAmount,
    isSupplierFee: row?.isSupplierFee,
  }));
}

export function getBookingRoot(data) {
  const candidates = [
    data?.flightObj,
    data?.flightBooking,
    Array.isArray(data?.flightBooking) ? data.flightBooking[0] : null,
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    const booking = Array.isArray(raw) ? raw[0] : raw;
    if (booking && (booking.adult || booking.child || booking.infant)) {
      return booking;
    }
    if (booking && typeof booking === "object" && !Array.isArray(booking)) {
      return booking;
    }
  }
  return {};
}

/** Original booking passenger-type unit price (sold value ÷ original count). */
export function getUnitPassengerPrice(data, travelerType) {
  const booking = getBookingRoot(data);
  const normalized = normalizeRefundTravelerType(travelerType);
  const pricing =
    {
      ADT: booking?.adult || {},
      CHD: booking?.child || {},
      INF: booking?.infant || {},
    }[normalized] || {};
  const originalCount = Math.max(Number(pricing?.passengerCount) || 1, 1);
  return {
    normalized,
    originalCount,
    unitPrice: roundMoney(parseMoneyField(pricing?.totalPrice) / originalCount),
    unitBase: roundMoney(parseMoneyField(pricing?.basePrice) / originalCount),
    unitTax: roundMoney(parseMoneyField(pricing?.taxes) / originalCount),
  };
}

/** How many of this type are selected on the refund request. */
export function countSelectedPassengersOfType(data, travelerType) {
  const booking = getBookingRoot(data);
  const passengerIds = (data?.passengerIds || []).map(String);
  const travellers = booking?.travelers || [];
  const normalized = normalizeRefundTravelerType(travelerType);
  const matched = travellers.filter(
    (t) =>
      passengerIds.includes(String(t?.id)) &&
      normalizeRefundTravelerType(t?.travelerType) === normalized,
  );
  return Math.max(matched.length, 0);
}

export function getTotalTravelerCount(data) {
  const booking = getBookingRoot(data);
  const fromTravelers = (booking?.travelers || []).length;
  if (fromTravelers > 0) return fromTravelers;
  return (
    Number(booking?.adult?.passengerCount || 0) +
    Number(booking?.child?.passengerCount || 0) +
    Number(booking?.infant?.passengerCount || 0)
  );
}

/**
 * Ticketing fee share retained for selected passengers (contract §4 step 3).
 * Not subtracted again from passenger-type totals when those totals exclude it.
 */
export function getTicketingFeeRetained(data, selectedTravelerCount) {
  const booking = getBookingRoot(data);
  const ticketingFee = Number(booking?.ticketingFee || data?.ticketingFee || 0);
  const totalTravelers = Math.max(getTotalTravelerCount(data), 1);
  const perPassenger = ticketingFee / totalTravelers;
  const selected = Math.max(Number(selectedTravelerCount) || 0, 0);
  return {
    ticketingFee,
    totalTravelers,
    ticketingFeePerPassenger: roundMoney(perPassenger),
    ticketingFeeRetained: roundMoney(perPassenger * selected),
  };
}

function applyAdjustmentCaps(held, adjustment) {
  let value = Math.max(0, Number(held) || 0);
  const maxCap = adjustment?.maxCap ?? adjustment?.MaxCap;
  const minCap = adjustment?.minCap ?? adjustment?.MinCap;
  if (maxCap != null && maxCap !== "" && Number.isFinite(Number(maxCap))) {
    value = Math.min(value, Number(maxCap));
  }
  if (minCap != null && minCap !== "" && Number.isFinite(Number(minCap))) {
    value = Math.max(value, Number(minCap));
  }
  return roundMoney(value);
}

/**
 * Booking appliedAdjustments (Markup/Markdown, %/Fixed).
 *
 * Intended refund ladder:
 * 1) % applies to **base fare only** (not tax / not sold total).
 * 2) Strip that slice from the sold unit → original unit price.
 * 3) Deductible / Base+Tax cap use that original unit.
 * 4) After returns − fees, **add the removed markup back** (Markdown subtracts).
 *
 * Sold `basePrice` is treated as already including the % adjustment, so:
 *   originalBase = base / (1 + rate)   // Markup
 *   held         = base − originalBase // = base × rate/(1+rate)
 *
 * @param {number} unitPassengerPrice sold unit (totalPrice / count)
 * @param {object|null} adjustment appliedAdjustments entry
 * @param {number|null} unitBase sold unit base (basePrice / count)
 */
export function reverseOriginalAdjustment(
  unitPassengerPrice,
  adjustment,
  unitBase = null,
) {
  const unit = Math.max(0, Number(unitPassengerPrice) || 0);
  const base = Math.max(
    0,
    Number(unitBase != null && unitBase !== "" ? unitBase : 0) || 0,
  );
  if (!adjustment) {
    return {
      underlyingValue: unit,
      heldAdjustment: 0,
      type: null,
      method: null,
    };
  }
  const type = String(adjustment.type || adjustment.Type || "Markup");
  const method = String(adjustment.method || adjustment.Method || "Percentage");
  const amount = Number(adjustment.amount ?? adjustment.Amount ?? 0);

  if (method === "Percentage" || method === "percentage") {
    const rate = amount / 100;
    // Strip % out of base only — fall back to unit when base missing
    const basis = base > 0 ? base : unit;
    if (type === "Markdown" || type === "markdown") {
      if (rate >= 1) {
        return {
          underlyingValue: unit,
          heldAdjustment: 0,
          type: "Markdown",
          method: "Percentage",
        };
      }
      // Sold base is marked down; restore original base = basis / (1 - rate)
      const originalBase = roundMoney(basis / (1 - rate));
      const heldAdjustment = applyAdjustmentCaps(
        originalBase - basis,
        adjustment,
      );
      return {
        underlyingValue: roundMoney(unit + heldAdjustment),
        heldAdjustment,
        type: "Markdown",
        method: "Percentage",
      };
    }
    // Sold base includes markup; original base = basis / (1 + rate)
    const originalBase = roundMoney(basis / (1 + rate));
    const heldAdjustment = applyAdjustmentCaps(
      basis - originalBase,
      adjustment,
    );
    return {
      underlyingValue: roundMoney(Math.max(0, unit - heldAdjustment)),
      heldAdjustment,
      type: "Markup",
      method: "Percentage",
    };
  }

  // Fixed — per passenger
  const fixed = applyAdjustmentCaps(Math.max(0, amount), adjustment);
  if (type === "Markdown" || type === "markdown") {
    return {
      underlyingValue: roundMoney(unit + fixed),
      heldAdjustment: fixed,
      type: "Markdown",
      method: "Fixed",
    };
  }
  return {
    underlyingValue: roundMoney(Math.max(0, unit - fixed)),
    heldAdjustment: Math.min(unit, fixed),
    type: "Markup",
    method: "Fixed",
  };
}

/** Prefer Flight-scoped Active adjustment from appliedAdjustments. */
export function resolveOriginalAppliedAdjustment(data) {
  const booking = getBookingRoot(data);
  const list = Array.isArray(booking?.appliedAdjustments)
    ? booking.appliedAdjustments
    : Array.isArray(data?.appliedAdjustments)
      ? data.appliedAdjustments
      : [];
  const active = list.filter(
    (a) => !a?.status || String(a.status).toLowerCase() === "active",
  );
  const flightScoped =
    active.find((a) => /flight/i.test(String(a?.appliedTo || "Flight"))) ||
    active[0] ||
    null;
  return flightScoped;
}

/**
 * Build breakdown with Deductible present (auto if missing).
 * Deductible = original unit (sold − base markup held) − Base − Tax.
 */
export function breakdownWithDeductible(breakDown, deductibleBasis) {
  const rows = Array.isArray(breakDown) ? [...breakDown] : [];
  const basis = Math.max(0, Number(deductibleBasis) || 0);
  const baseAgent = rows.reduce((sum, row) => {
    if (row?.isDeducted === false && isRefundAmountLine(row?.name)) {
      return sum + Number(row?.amount || 0);
    }
    return sum;
  }, 0);
  const taxAgent = rows.reduce((sum, row) => {
    if (row?.isDeducted === false && isRefundTaxLine(row?.name)) {
      return sum + Number(row?.amount || 0);
    }
    return sum;
  }, 0);
  const baseAirline = rows.reduce((sum, row) => {
    if (row?.isDeducted === false && isRefundAmountLine(row?.name)) {
      return sum + Number(row?.airlineAmount ?? row?.amount ?? 0);
    }
    return sum;
  }, 0);
  const taxAirline = rows.reduce((sum, row) => {
    if (row?.isDeducted === false && isRefundTaxLine(row?.name)) {
      return sum + Number(row?.airlineAmount ?? row?.amount ?? 0);
    }
    return sum;
  }, 0);

  const withoutOldDed = rows.filter((row) => !isComputedDeductibleLine(row));
  withoutOldDed.push({
    name: REFUND_DEDUCTIBLE_NAME,
    amount: roundMoney(basis - baseAgent - taxAgent),
    airlineAmount: roundMoney(basis - baseAirline - taxAirline),
    isDeducted: true,
  });
  return withoutOldDed;
}

/** Base Return + Tax Return (airline or agent amounts). */
export function sumReturnLines(breakDown, { useAirline = false } = {}) {
  return roundMoney(
    (breakDown || []).reduce((sum, row) => {
      if (row?.isDeducted !== false) return sum;
      if (!isRefundAmountLine(row?.name) && !isRefundTaxLine(row?.name)) {
        return sum;
      }
      const value = useAirline
        ? Number(row?.airlineAmount ?? row?.amount ?? 0)
        : Number(row?.amount || 0);
      return sum + value;
    }, 0),
  );
}

/** Penalty + Supplier Fee only (excludes auto Deductible). */
export function sumOpsFees(breakDown, { useAirline = false } = {}) {
  return roundMoney(
    (breakDown || []).reduce((sum, row) => {
      if (isComputedDeductibleLine(row)) return sum;
      if (row?.isDeducted === false) return sum;
      const value = useAirline
        ? Number(row?.airlineAmount ?? row?.amount ?? 0)
        : Number(row?.amount || 0);
      return sum + value;
    }, 0),
  );
}

/** Σ breakDown.amount where isDeducted === true (contract §4 step 6). */
export function sumPaxDeductable(breakDown, { useAirline = false } = {}) {
  return roundMoney(
    (breakDown || []).reduce((sum, row) => {
      if (row?.isDeducted === false) return sum;
      const value = useAirline
        ? Number(row?.airlineAmount ?? row?.amount ?? 0)
        : Number(row?.amount || 0);
      return sum + value;
    }, 0),
  );
}

/**
 * Per passenger type.
 * 1) Strip booking % from base → original unit (FE deductible / cap).
 * 2) Deductible = original unit − Base − Tax.
 * 3) Estimate = (agent Base + Tax − fees) ± markup add-back.
 *    Markup add-back: backend Intraverse Markup if present, else FE estimate.
 */
export function computePassengerRefund({
  unitPrice,
  breakDown,
  adjustment,
  unitBase = null,
  intraverseMarkup = null,
}) {
  const unit = roundMoney(unitPrice);
  const { underlyingValue } = reverseOriginalAdjustment(
    unit,
    adjustment,
    unitBase,
  );
  const markupAddBack = resolveRefundMarkupAddBack({
    breakDown,
    unitPrice: unit,
    unitBase,
    adjustment,
    intraverseMarkup,
  });
  const { heldAdjustment, type } = markupAddBack;

  // Deductible on original unit (after stripping base markup)
  const fullBreakdown = breakdownWithDeductible(breakDown, underlyingValue);
  const paxDeductable = sumPaxDeductable(fullBreakdown);
  const airlineDeductable = sumPaxDeductable(fullBreakdown, {
    useAirline: true,
  });
  const deductibleField = roundMoney(
    (fullBreakdown || []).reduce((sum, row) => {
      if (!isComputedDeductibleLine(row)) return sum;
      return sum + Number(row?.airlineAmount ?? row?.amount ?? 0);
    }, 0),
  );

  const airlineReturns = sumReturnLines(fullBreakdown, { useAirline: true });
  const agentReturns = sumReturnLines(fullBreakdown);
  const airlineFees = sumOpsFees(fullBreakdown, { useAirline: true });
  const agentFees = sumOpsFees(fullBreakdown);

  const airlineSide = airlineReturns;
  const agentSide = agentReturns;

  // Add markup back (Markdown subtracts). Cap at sold unit.
  const applyHeld = (returns, fees) => {
    let net = roundMoney(returns - fees);
    if (type === "Markdown") {
      net = roundMoney(net - heldAdjustment);
    } else if (type === "Markup") {
      net = roundMoney(net + heldAdjustment);
    }
    return Math.max(0, Math.min(unit, net));
  };

  const passengerRefund = applyHeld(agentReturns, agentFees);
  const airlinePassengerRefund = applyHeld(airlineReturns, airlineFees);

  return {
    unitPrice: unit,
    underlyingValue,
    heldAdjustment,
    adjustmentType: type,
    markupSource: markupAddBack.source,
    markupLabel: markupAddBack.label,
    paxDeductable,
    airlineDeductable,
    /** Auto Deductible line only (original unit − Base − Tax). */
    deductibleField,
    airlineSide,
    agentSide,
    airlineFees,
    agentFees,
    passengerRefund,
    airlinePassengerRefund,
    breakdown: fullBreakdown,
    soldMinusDeductions: roundMoney(unit - paxDeductable),
  };
}

/**
 * Full request summary per Refund Calculation Product Contract.
 * finalRefundAmount = Σ(passengerRefund × selectedCount)
 * Ticketing fee retained is recorded separately — not subtracted again.
 */
export function getRefundQuoteSummary(data, passengerPriceOverride) {
  const passengerPrice = Array.isArray(passengerPriceOverride)
    ? passengerPriceOverride
    : Array.isArray(data?.passengerPrice)
      ? data.passengerPrice
      : [];
  const adjustment = resolveOriginalAppliedAdjustment(data);
  const currency =
    data?.paymentCurrency || data?.currency || getBookingRoot(data)?.currency;

  const unique = passengerPrice.filter(
    (item, i, arr) =>
      arr.findIndex(
        (other) =>
          normalizeRefundTravelerType(other?.travelerType) ===
          normalizeRefundTravelerType(item?.travelerType),
      ) === i,
  );

  let selectedTravelerCount = 0;
  let refundRunSum = 0;
  let totalPaxDeductable = 0;
  let totalDeductibleField = 0;
  let totalOpsFees = 0;
  let totalHeldAdjustment = 0;
  let finalRefundAmount = 0;
  let airlineRefundAmount = 0;
  /** Airline refund on original fare (before booking markup add-back). */
  let airlineSideTotal = 0;

  const rows = unique.map((item, idx) => {
    const travelerType = normalizeRefundTravelerType(item?.travelerType);
    const { unitPrice, unitBase } = getUnitPassengerPrice(data, travelerType);
    let selectedCount = countSelectedPassengersOfType(data, travelerType);
    if (selectedCount <= 0) {
      selectedCount = Math.max(Number(item?.passengerCount) || 1, 1);
    }
    selectedTravelerCount += selectedCount;
    refundRunSum = roundMoney(refundRunSum + unitPrice * selectedCount);

    const breakDown = item?.breakDown || item?.breakdown || [];
    const computed = computePassengerRefund({
      unitPrice,
      unitBase,
      breakDown,
      adjustment,
      intraverseMarkup:
        item?.intraverseMarkup ?? getIntraverseMarkupFromBreakdown(breakDown),
    });

    totalPaxDeductable = roundMoney(
      totalPaxDeductable + computed.paxDeductable * selectedCount,
    );
    totalDeductibleField = roundMoney(
      totalDeductibleField + computed.deductibleField * selectedCount,
    );
    totalOpsFees = roundMoney(
      totalOpsFees + computed.airlineFees * selectedCount,
    );
    totalHeldAdjustment = roundMoney(
      totalHeldAdjustment + computed.heldAdjustment * selectedCount,
    );
    finalRefundAmount = roundMoney(
      finalRefundAmount + computed.passengerRefund * selectedCount,
    );
    airlineRefundAmount = roundMoney(
      airlineRefundAmount + computed.airlinePassengerRefund * selectedCount,
    );
    airlineSideTotal = roundMoney(
      airlineSideTotal + computed.airlineSide * selectedCount,
    );

    return {
      key: idx,
      travelerType,
      count: selectedCount,
      breakdown: computed.breakdown,
      unitPrice: computed.unitPrice,
      underlyingValue: computed.underlyingValue,
      heldAdjustment: computed.heldAdjustment,
      markupSource: computed.markupSource,
      markupLabel: computed.markupLabel,
      paxDeductable: computed.paxDeductable,
      deductibleField: computed.deductibleField,
      passengerRefund: computed.passengerRefund,
      airlineSide: computed.airlineSide,
      airlinePassengerRefund: computed.airlinePassengerRefund,
      airlineNet: computed.airlinePassengerRefund,
      agentNet: computed.passengerRefund,
      markupKeep: roundMoney(
        Math.max(0, computed.airlinePassengerRefund - computed.passengerRefund),
      ),
      airlineSubtotal: roundMoney(
        computed.airlinePassengerRefund * selectedCount,
      ),
      agentSubtotal: roundMoney(computed.passengerRefund * selectedCount),
      markupSubtotal: roundMoney(
        Math.max(
          0,
          computed.airlinePassengerRefund - computed.passengerRefund,
        ) * selectedCount,
      ),
    };
  });

  const feeInfo = getTicketingFeeRetained(data, selectedTravelerCount);
  // Configured refund service fee (separate from booking ticketingFee).
  // Contract final refund does not subtract this; show for Ops visibility only
  // unless backend still expects it off the agent payable.
  const serviceFee = Number(data?.intraverseFee ?? 0) || 0;

  const payable =
    Number(data?.payable ?? data?.paymentAmount ?? data?.pricedAmountNgn) ||
    finalRefundAmount;

  return {
    rows,
    adjustment,
    refundRunSum,
    totalPaxDeductable,
    /** Auto Deductible only (original fare − Base − Tax) × pax — what we send. */
    totalDeductibleField,
    totalOpsFees,
    totalHeldAdjustment,
    finalRefundAmount,
    /** After booking markup/markdown reverse (airline figures). */
    airlineRefund: airlineRefundAmount,
    /** Before booking markup add-back — matches quote ladder step 3. */
    airlineSideTotal,
    // Aliases used by existing UI
    agentRefund: finalRefundAmount,
    agentBeforeFee: finalRefundAmount,
    markupKeep: rows.reduce((s, r) => s + r.markupSubtotal, 0),
    serviceFee,
    platformKeep: rows.reduce((s, r) => s + r.markupSubtotal, 0) + serviceFee,
    ticketingFeeRetained: feeInfo.ticketingFeeRetained,
    ticketingFeePerPassenger: feeInfo.ticketingFeePerPassenger,
    payable,
    currency,
    refundableValue: finalRefundAmount,
    netRefund: finalRefundAmount,
    // Sanity: refund_run_sum − total_pax_deductable
    sanityCheck: roundMoney(refundRunSum - totalPaxDeductable),
  };
}

/** @deprecated prefer getRefundQuoteSummary */
export function getRefundQuoteTotals(data) {
  const summary = getRefundQuoteSummary(data);
  return {
    rows: summary.rows,
    refundableValue: summary.refundableValue,
    serviceFee: summary.serviceFee,
    netRefund: summary.netRefund,
  };
}
